import carriers from '../../../../static/data/carriers.json';
import { document } from '../../../../models/entities/common_subentities/document';
import { discussion } from '../../../../models/entities/common_subentities/discussion';
import { event } from '../../../../models/entities/common_subentities/event';
import { history } from '../../../../models/entities/common_subentities/history';
import { shipment } from '../../../../models/entities/shipment/shipment';
import { eShipmentTypes } from '../../../../models/entities/shipment/shipmentTypes';
import { ColumnType } from '../../../shared/Grid/types/Column';

export type ShipmentContext = {
    shipmentId: string;
    shipmentNumber: string;
};

export type ConsolidationEventRow = event & ShipmentContext;
export type ConsolidationDocumentRow = document & ShipmentContext;
export type ConsolidationDiscussionRow = discussion & ShipmentContext;

const numberOrZero = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/** Keep shipment link as sticky first column so headers align with cells in consolidation grids. */
export const prioritizeShipmentLinkColumn = <T>(columns: Array<ColumnType<T>>) => {
    const linkIndex = columns.findIndex((col) => col.columnType === 'shipmentLink');
    if (linkIndex <= 0) return columns;
    const reordered = [...columns];
    const [linkColumn] = reordered.splice(linkIndex, 1);
    return [linkColumn, ...reordered];
};

export const getCarrierName = (carrierCode?: string) => {
    if (!carrierCode) return '-';
    const carrier = carriers.find((item) => item.carrierCode === carrierCode);
    return carrier?.carrierName || carrierCode;
};

const hexToRgb = (hex: string) => {
    const value = hex.replace('#', '');
    return {
        r: parseInt(value.substring(0, 2), 16),
        g: parseInt(value.substring(2, 4), 16),
        b: parseInt(value.substring(4, 6), 16)
    };
};

const colorLuminance = (hex: string) => {
    const { r, g, b } = hexToRgb(hex);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

/** Light pill background + readable status text (matches consolidation house table mockup). */
export const getStatusPillStyle = (color?: string): { background: string; color: string } => {
    const fallback = { background: '#f0f0f0', color: '#5a5a5a' };
    if (!color) return fallback;

    const hex = color.startsWith('#') ? color : `#${color}`;
    if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return fallback;

    if (colorLuminance(hex) > 0.55) {
        return { background: '#f0f0f0', color: '#5a5a5a' };
    }

    return { background: `${hex}22`, color: hex };
};

export const getShipmentPieces = (shipment: shipment) => {
    const pieces = shipment.quantity ?? shipment.totalCartons ?? shipment.counts?.packageCount;
    return pieces != null && String(pieces).trim() !== '' ? Number(pieces) : null;
};

/** Title segment after "Consolidation (...)" — driven by shipment typeCode, not hardcoded to Air. */
export const getConsolidationTitleSuffix = (groupedShipments: shipment[]) => {
    const types = [...new Set(groupedShipments.map((s) => s.typeCode))];
    if (types.length !== 1) return 'Mixed';
    const code = types[0];
    if (code === 'EXPRESS') return 'Express';
    if (['AE', 'AI'].includes(code)) return 'Air';
    if (code === 'DROP_AIR') return 'Drop Air';
    if (['OI', 'OE', 'OEX'].includes(code)) return 'Ocean';
    if (code === 'DROP_OCEAN') return 'Drop Ocean';
    return eShipmentTypes[code] || code;
};

/**
 * Same visibility rules as the consolidations list:
 * - at least 2 shipments share the master
 * - group type is Air (AE/AI) or Ocean (OI/OE/OEX)
 */
export const isAirOrOceanConsolidationGroup = (groupedShipments: shipment[]): boolean => {
    if (groupedShipments.length <= 1) return false;
    const kind = getConsolidationTitleSuffix(groupedShipments);
    return kind === 'Air' || kind === 'Ocean';
};

const pickFirstDate = (dates: Array<Date | undefined>, mode: 'earliest' | 'latest') => {
    const valid = dates
        .filter((d): d is Date => !!d && !Number.isNaN(new Date(d).getTime()))
        .map((d) => new Date(d).getTime());
    if (!valid.length) return undefined;
    const picked = mode === 'earliest' ? Math.min(...valid) : Math.max(...valid);
    return new Date(picked);
};

const pickRouteShipment = (groupedShipments: shipment[]) => {
    return (
        groupedShipments.find((s) => s.originStation && s.destinationStation) ||
        groupedShipments.find((s) => s.originStation || s.destinationStation) ||
        groupedShipments[0]
    );
};

/** Shipment opened by "Edit Master" — prefer record without house AWB, else earliest created. */
export const pickMasterShipmentForEdit = (groupedShipments: shipment[]) => {
    const withoutHouse = groupedShipments.find((s) => !s.house || String(s.house).trim() === '' || s.house === '-');
    if (withoutHouse) return withoutHouse;

    return [...groupedShipments].sort(
        (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
    )[0];
};

export const buildConsolidationSummary = (groupedShipments: shipment[], master: string) => {
    const firstShipment = groupedShipments[0];
    const routeShipment = pickRouteShipment(groupedShipments);
    const earliestShipment = [...groupedShipments].sort(
        (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
    )[0];

    const totalWeight = groupedShipments.reduce((sum, s) => sum + numberOrZero(s.weight), 0);
    const totalVolume = groupedShipments.reduce((sum, s) => sum + numberOrZero(s.volume), 0);
    const totalPieces = groupedShipments.reduce((sum, s) => sum + (getShipmentPieces(s) || 0), 0);

    const flightOrVoyage =
        groupedShipments.map((s) => s.voyageNumber || s.vesselName).find((v) => !!v && String(v).trim()) || '-';

    const carrierShipment = groupedShipments.find((s) => s.carrierCode) || firstShipment;
    const handlingAgentId = groupedShipments.map((s) => s.handlingAgent).find((id) => !!id && String(id).trim());
    const commodity =
        groupedShipments.map((s) => s.commodity).find((c) => !!c && String(c).trim()) || '-';

    const etd = pickFirstDate(
        groupedShipments.map((s) => s.etd),
        'earliest'
    );
    const eta = pickFirstDate(
        groupedShipments.map((s) => s.eta),
        'latest'
    );

    const titleSuffix = getConsolidationTitleSuffix(groupedShipments);
    const consolidationTypeCode =
        titleSuffix === 'Air' ? 'AE' : titleSuffix === 'Ocean' ? 'OE' : groupedShipments[0]?.typeCode;
    const typeLabel =
        titleSuffix === 'Air'
            ? 'Air Consolidation'
            : titleSuffix === 'Ocean'
            ? 'Ocean Consolidation'
            : groupedShipments.length === 1
            ? `${eShipmentTypes[groupedShipments[0].typeCode] || 'Consolidation'} Consolidation`
            : 'Consolidation';

    return {
        master,
        status: firstShipment?.state || '-',
        route: `${routeShipment?.originStation || '-'} → ${routeShipment?.destinationStation || '-'}`,
        originStation: routeShipment?.originStation,
        destinationStation: routeShipment?.destinationStation,
        originStationName: routeShipment?.originStationName,
        destinationStationName: routeShipment?.destinationStationName,
        originCountry: routeShipment?.originCountry,
        destinationCountry: routeShipment?.destinationCountry,
        carrierCode: carrierShipment?.carrierCode,
        consolidationTypeCode,
        flightOrVoyage,
        carrier: getCarrierName(carrierShipment?.carrierCode),
        handlingAgentId: handlingAgentId || undefined,
        commodity,
        etd,
        eta,
        shipmentCount: groupedShipments.length,
        totalWeight,
        totalVolume,
        totalPieces,
        weightUnit: firstShipment?.weightUnit || 'KG',
        volumeUnit: firstShipment?.volumeUnit || 'CBM',
        typeLabel,
        titleSuffix,
        createdAt: earliestShipment?.createdAt,
        createdBy: earliestShipment?.userName || '-',
        incoterms: firstShipment?.incoterms
    };
};

/** Prefill new house shipment form from consolidation summary (Task 5).
 *
 * Must use ShipmentForm.tsx Formik field names (see default initialValues in ShipmentForm.tsx):
 * | Consolidation data     | Form field name    | Wrong keys (ignored by form) |
 * |------------------------|--------------------|--------------------------------|
 * | Shipment type (AE/OE…) | shipment_type      | type_code                      |
 * | Carrier                | carrier            | carrier_code                   |
 * | Handling agent BP id   | handling_agent     | —                              |
 * | Master AWB             | master             | —                              |
 * | Origin / destination   | origin, destination| —                              |
 * | Countries              | origin_country, destination_country | —       |
 * | Incoterms              | incoterms          | —                              |
 * | Flight / voyage        | voyage_number, vessel_name | —                  |
 * | ETD / ETA              | etd, eta           | —                              |
 */
export const buildHousePrefillInitialValues = (
    summary: ReturnType<typeof buildConsolidationSummary>,
    master: string,
    referenceShipment?: shipment
) => {
    const flight =
        summary.flightOrVoyage && summary.flightOrVoyage !== '-' ? summary.flightOrVoyage : '';
    const commodityFromSummary = summary.commodity && summary.commodity !== '-' ? summary.commodity : '';
    const commodityFromRef = referenceShipment?.commodity?.trim() || '';

    return {
        master,
        shipment_type: summary.consolidationTypeCode,
        handling_agent: summary.handlingAgentId || referenceShipment?.handlingAgent || '',
        co_loader: referenceShipment?.coLoader || '',
        origin: summary.originStation || referenceShipment?.originStation || '',
        destination: summary.destinationStation || referenceShipment?.destinationStation || '',
        origin_country: summary.originCountry || referenceShipment?.originCountry,
        destination_country: summary.destinationCountry || referenceShipment?.destinationCountry,
        origin_zipcode: referenceShipment?.originZipCode || '',
        destination_zipcode: referenceShipment?.destinationZipCode || '',
        origin_pickup_address: referenceShipment?.originPickupAddress || '',
        destination_pickup_address: referenceShipment?.destinationPickupAddress || '',
        carrier: summary.carrierCode || referenceShipment?.carrierCode || '',
        incoterms: referenceShipment?.incoterms || summary.incoterms,
        voyage_number: flight || referenceShipment?.voyageNumber || '',
        vessel_name: flight || referenceShipment?.vesselName || '',
        etd: summary.etd || referenceShipment?.etd,
        eta: summary.eta || referenceShipment?.eta,
        sub_type: referenceShipment?.subTypeCode || '',
        commodity: commodityFromRef || commodityFromSummary,
        commodity_type: referenceShipment?.commodityType || '',
        customs_file: referenceShipment?.customsFile || '',
        operator: referenceShipment?.operator || '',
        forwarderId: referenceShipment?.forwarderId ?? null,
        cargoOwnerId: referenceShipment?.cargoOwnerId ?? null,
        brokerId: referenceShipment?.brokerId ?? null,
        companyId: referenceShipment?.companyId || '',
        companyName: referenceShipment?.companyName || '',
        weightUnit: summary.weightUnit,
        volumeUnit: summary.volumeUnit,
        mawb_type:
            summary.consolidationTypeCode === 'EXPRESS'
                ? referenceShipment?.mawbType || 'AG'
                : referenceShipment?.mawbType || null
    };
};

export const buildCommodityBreakdown = (groupedShipments: shipment[]) => {
    const counts: Record<string, number> = {};
    groupedShipments.forEach((s) => {
        const key = (s.commodity || 'General').trim() || 'General';
        counts[key] = (counts[key] || 0) + 1;
    });
    const total = groupedShipments.length || 1;
    return Object.entries(counts)
        .map(([name, value]) => ({
            name,
            value,
            percent: Math.round((value / total) * 100)
        }))
        .sort((a, b) => b.value - a.value);
};

export const aggregateDocuments = (groupedShipments: shipment[]): ConsolidationDocumentRow[] => {
    return groupedShipments.flatMap((s) =>
        (s.documents?.documents || []).map((doc, index) => ({
            ...doc,
            id: `${s.id}-${doc.id}-${index}` as unknown as number,
            shipmentId: s.id,
            shipmentNumber: s.CargoZoneNumber
        }))
    );
};

export const aggregateDocumentCountsByType = (groupedShipments: shipment[]) => {
    const docs = aggregateDocuments(groupedShipments);
    const byType: Record<string, number> = {};
    docs.forEach((doc) => {
        const type = Array.isArray(doc.type) ? doc.type.join(', ') : doc.type || 'Other';
        byType[type] = (byType[type] || 0) + 1;
    });
    return {
        total: docs.length,
        houseAwbs: groupedShipments.filter((s) => s.house).length,
        byType: Object.entries(byType).map(([type, count]) => ({ type, count }))
    };
};

export const aggregateEvents = (groupedShipments: shipment[]): ConsolidationEventRow[] => {
    return groupedShipments
        .flatMap((s) =>
            (s.events || []).map((ev, index) => ({
                ...ev,
                id: `${s.id}-${ev.id}-${index}` as unknown as number,
                shipmentId: s.id,
                shipmentNumber: s.CargoZoneNumber
            }))
        )
        .sort((a, b) => new Date(b.eventDate || b.createdAt || 0).getTime() - new Date(a.eventDate || a.createdAt || 0).getTime());
};

export const aggregateHistory = (groupedShipments: shipment[]): history[] => {
    return groupedShipments
        .flatMap((s) => s.history || [])
        .sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime());
};

export const aggregateDiscussions = (groupedShipments: shipment[]): ConsolidationDiscussionRow[] => {
    return groupedShipments
        .flatMap((s) =>
            (s.discussions || []).map((d) => ({
                ...d,
                shipmentId: s.id,
                shipmentNumber: s.CargoZoneNumber
            }))
        )
        .sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime());
};
